import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import {
  CalendarModel,
  CalendarMonthModel,
  CalendarYearModel,
  CalendarDayModel,
  MONTHSHORT,
  CALENDARVIEW,
} from 'src/app/core/models/calendar.model';

import { MOCK_SCHEDULE_RESULTS } from 'src/app/core/constants/calendar';

@Component({
  selector: 'app-calendar-view',
  templateUrl: './calendar-view.component.html',
  styleUrls: ['./calendar-view.component.scss']
})
export class CalendarViewComponent implements OnInit, OnDestroy {

  @Input() calendar: CalendarModel;

  private activeViewMode: CALENDARVIEW;
  @Input() set viewMode(mode: CALENDARVIEW) {
    this.activeViewMode = mode;
    if (this.activeViewMode === CALENDARVIEW.Results) {
      this.prepareResults();
      return;
    }
    this.prepareCalendar();
  }
  get viewMode(): CALENDARVIEW {
    return this.activeViewMode;
  }

  private activeViewYear: number;
  @Input() set activeYear(y: number) {
    this.activeViewYear = y;
    this.prepareCalendar();
  }
  get activeYear(): number {
    return this.activeViewYear;
  }

  private activeViewMonth: number;
  @Input() set activeMonth(m: number) {
    this.activeViewMonth = m;
    this.prepareCalendar();
  }
  get activeMonth(): number {
    return this.activeViewMonth;
  }

  private activeViewDay: number;
  @Input() set activeDay(d: number) {
    this.activeViewDay = d;
    this.prepareCalendar();
  }
  get activeDay(): number {
    return this.activeViewDay;
  }

  @Output() openMark: EventEmitter<any> = new EventEmitter();
  @Output() openNewMark: EventEmitter<any> = new EventEmitter();

  viewMonth: CalendarMonthModel;
  viewWeekIdx: number;
  viewDayIdx: number;

  weekDays = [ 'Dom.', 'Seg.', 'Ter.', 'Qua.', 'Qui.', 'Sex.', 'Sáb.' ];
  dayHours = [
    '06:00', '07:00', '08:00', '09:00', '10:00', '11:00',
    '12:00', '13:00', '14:00', '15:00', '16:00', '17:00',
    '18:00', '19:00', '20:00', '21:00', '22:00'
  ];

  headerHeight = [ 90, 108 ];

  get headerHeightCalc(): number {
    return this.verifyHeaderHeight();
  }

  private dt: Date;
  set dtUpdate(date: Date) {
    this.dt = date;
  }
  get calcLineYpos(): number {
    return (( new Date().getHours() + new Date().getMinutes() / 60 ) - 6 ) * 56 - 8;
  }

  timer: any;

  // mock
  searchFilterResults = MOCK_SCHEDULE_RESULTS.activities;

  ngOnInit(): void {
    this.prepareCalendar();
    this.startTimer();
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  startTimer(): void {
    this.timer = window.setInterval(() => {
      this.dtUpdate = new Date();
    }, 1000);
  }

  stopTimer(): void {
    window.clearInterval(this.timer);
  }

  prepareCalendar(): void {
    this.setViewMonth();
    switch (this.viewMode) {
      case CALENDARVIEW.Week:
        this.setViewWeek();
        break;
      case CALENDARVIEW.Day:
        this.setViewDay();
        break;
    }
  }

  prepareResults(): void {
    console.log('using mock results');
  }

  setViewMonth(): void {
    this.calendar.years.forEach((y: CalendarYearModel, yi: number) => {
      if (y.year === this.activeViewYear) {
        y.months.forEach((m: CalendarMonthModel, mi: number) => {
          if (m.month === this.activeViewMonth) {
            this.viewMonth = JSON.parse(JSON.stringify(this.calendar.years[yi].months[mi]));
            this.completeEdges(yi, mi);
            return;
          }
        });
      }
    });
  }

  setViewWeek(): void {
    this.calendar.years[1].months[this.activeMonth].weeks.forEach((week, idx: number) => {
      for (const weekday of week.days) {
        if (weekday.day === this.activeDay) {
          this.viewWeekIdx = idx;
          return;
        }
      }
    });
  }

  setViewDay(): void {
    this.calendar.years[1].months[this.activeMonth].weeks.forEach((week, idx: number) => {
      for (let i = 0; i < week.days.length; i++) {
        if (week.days[i].day === this.activeDay) {
          this.viewWeekIdx = idx;
          this.viewDayIdx = i;
          return;
        }
      }
    });
  }

  completeEdges(yi: number, mi: number): void {
    const viewMonthFirstWeekLen = this.calendar.years[yi].months[mi].weeks[0].days.length;
    const viewMonthLastWeekLen = this.calendar.years[yi].months[mi].weeks[this.calendar.years[yi].months[mi].weeks.length - 1].days.length;
    const viewMonthLastWeekIdx = this.calendar.years[yi].months[mi].weeks.length - 1;
    let nextMonthIdx = mi + 1;
    let lastMonthIdx = mi - 1;
    let nextYearIdx = yi;
    let lastYearIdx = yi;
    if (mi === 0) {
      lastMonthIdx = 11;
      lastYearIdx = yi - 1;
    }
    if (mi === 11) {
      nextMonthIdx = 0;
      nextYearIdx = yi + 1;
    }
    const firstWeekEdge = this.calendar.years[lastYearIdx].months[lastMonthIdx].weeks.length - 1;
    if (viewMonthFirstWeekLen < 7) {
      const iDays = JSON.parse(JSON.stringify(this.calendar.years[lastYearIdx].months[lastMonthIdx].weeks[firstWeekEdge].days));
      iDays.forEach(day => day.notActive = true);
      const d = iDays.concat(this.calendar.years[yi].months[mi].weeks[0].days);
      this.viewMonth.weeks[0].days = d;
    }
    if (viewMonthLastWeekLen < 7) {
      const iDays = JSON.parse(JSON.stringify(this.calendar.years[nextYearIdx].months[nextMonthIdx].weeks[0].days));
      iDays.forEach(day => day.notActive = true);
      this.viewMonth.weeks[viewMonthLastWeekIdx].days = this.viewMonth.weeks[viewMonthLastWeekIdx].days.concat(iDays);
    }
  }

  isToday(weekday: any): boolean {
    if (weekday.notActive
      || this.calendar.today.day !== weekday.day
      || this.calendar.today.month !== this.activeViewMonth
      || this.calendar.today.year !== this.activeViewYear) {
      return false;
    }
    return true;
  }

  openMarkModal(ev: any, markData: any): void {
    ev.stopPropagation();
    this.openMark.emit(markData);
  }

  createNewMark(weekDay?: CalendarDayModel): void {
    let month: number = this.activeMonth;
    if (weekDay && weekDay.notActive) {
      weekDay.day < 7 ? month-- : month++;
    }
    const day = weekDay ? weekDay.day : this.activeDay;
    this.openNewMark.emit({ day, month });
  }

  verifyHeaderHeight(): number {
    let height = 0;
    for (const weekday of this.viewMonth.weeks[this.viewWeekIdx].days) {
      if (weekday.special?.length > 1) {
        height = 1;
        break;
      }
    }
    return this.headerHeight[height];
  }

  calcMarkYpos(hourStart: string): number {
    const start = hourStart.split(':');
    return (( parseFloat(start[0]) + parseFloat(start[1]) / 60 ) - 6 ) * 56;
  }

  calcMarkYsize(hourStart: string, hourEnd?: string): number {
    let h = 16;
    if (!hourEnd) {
      return h;
    }
    const start = hourStart.split(':');
    const end = hourEnd.split(':');
    const hS = ( parseFloat(start[0]) + parseFloat(start[1]) / 60 );
    const hE = ( parseFloat(end[0]) + parseFloat(end[1]) / 60 );
    h = ( hE - hS ) * 56;
    return h;
  }

  shortMonth(month: number): string {
    return MONTHSHORT[month];
  }

  shortYear(year: number): string {
    return String(year).substr(2);
  }

}
